import moment from 'moment';
import { createAction } from 'redux-actions';
import uuid from 'uuid/v4';
import {
  createParams,
  validateParams,
  getValueFromObjects,
  getCustomerAge,
  getCPRMapping,
  getAddressMappingV2,
  getMapping, prepareCommonCreateUpdateResponse,
} from './common';
import Product from './product';
import * as types from '../../constants/Api';
import { appErrorAction } from '../../actions/App';
import { createErrorBody, throwCustomError, throwGenericError } from '../error';
import { sanitizeString } from '../common';
import { isValidDate } from '../form/formValidators';

class DepositService extends Product {
  static TYPE = 'deposit';
  static COMPANY_INFO_BY_AGREEMENT_KEY = 'deposit/v1/company';
  static GET_PRICE = 'deposit/v1/price';
  static UPDATE_PRICE = 'deposit/v1/price';
  static CREATE_CUSTOMER = 'product/v1/customer';
  static DEPOSIT_PURCHASE = 'deposit/v1/purchase';
  static MINIMUM_DISCOUNT = 20;
  static PAYMENT_SERVICE_FEE_CODE = 'PBST';
  static GUARANTEE_FEE_CODE = 'GARB';
  static ELIGIBILITY_FAILED_ERROR_CODE = 'dk-mi-xcpr-0001';
  static validateCompanyInfo = createAction(
    types.DEPOSIT_COMPANY_INFO_VALID
  );
  static validatedCompanyInfo = createAction(
    types.DEPOSIT_COMPANY_INFO_VALIDATED
  );
  static createdDeposit = createAction(types.DEPOSIT_CREATED);
  static createDeposit = createAction(types.DEPOSIT_CREATE);
  static updatedDeposit = createAction(types.DEPOSIT_UPDATED);
  static updateDeposit = createAction(types.DEPOSIT_UPDATE);
  static preparedDeposit = createAction(types.DEPOSIT_PREPARED);
  static prepareDeposit = createAction(types.DEPOSIT_PREPARE);

  constructor() {
    super(
      DepositService.TYPE,
      DepositService.createDeposit,
      DepositService.updateDeposit,
      DepositService.prepareDeposit
    );
    super.prepareCreateUpdateResponse = this.prepareCreateUpdateResponse;
  }

  getDepositMapping() {
    return {
      product: {
        type: DepositService.TYPE,
      },
    };
  }

  validateCompanyInfoAction(params, formValues) {
    return (dispatch) =>
      dispatch(
        this.requestCompanyNameByAgreementKey(params, formValues)
      )
        .then((data) => {
          dispatch(this.validatedCompanyInfoAction(data));

          if (typeof formValues.setValues === 'function') {
            // We use setValues to update the form values back in react.
            formValues.setValues({ ...formValues, ...data });
          }
        })
        .catch((errorObj) => {
          if (errorObj.message === 'NOT_AUTHORIZED') {
            throwGenericError(errorObj, dispatch);
          } else {
            // Save state for service error.
            dispatch(
              this.validatedCompanyInfoAction({
                companyInfoValid: null,
                companyInfoNoService: true,
              })
            );

            if (typeof formValues.setValues === 'function') {
              // We use setValues to update the form values back in react.
              formValues.setValues({
                ...formValues,
                companyInfoValid: null,
                companyInfoNoService: true,
              });
            }
          }
        });
  }

  validatedCompanyInfoAction(payload) {
    return (dispatch) =>
      dispatch(DepositService.validatedCompanyInfo(payload));
  }

  createdDepositAction(payload) {
    return (dispatch) => dispatch(DepositService.createdDeposit(payload));
  }

  createDepositAction(mappedParams, reqModel, formValues) {
    return (dispatch) => {
      return dispatch(
        this.requestCreateProduct(
          DepositService.GET_PRICE,
          mappedParams,
          reqModel,
          formValues
        )
      )
        .then((data) => {
          if (this.isNotEligible(data)) {
            this.redirectToNotEligiblePage(dispatch);
            const errorMessage = { code: 277, message: 'Not eligible for purchasing Tryg Leje.' };
            throw errorMessage;
          } else {
            dispatch(this.createdDepositAction(data));
          }
        })
        .catch((data) => {
          dispatch(appErrorAction(data));
          return Promise.reject(data);
        });
    };
  }

  updatedDepositAction(payload) {
    return (dispatch) => dispatch(DepositService.updatedDeposit(payload));
  }

  updateDepositAction(mappedParams, reqModel, formValues) {
    return (dispatch) => {
      return dispatch(
        this.requestCreateProduct(
          DepositService.UPDATE_PRICE,
          mappedParams,
          reqModel,
          formValues
        )
      )
        .then((data) => {
          if (this.isNotEligible(data)) {
            this.redirectToNotEligiblePage(dispatch);
            const errorMessage = { code: 277, message: 'Not eligible for purchasing Tryg Leje.' };
            throw errorMessage;
          } else {
            dispatch(this.updatedDepositAction(data));
          }
        })
        .catch((data) => {
          dispatch(appErrorAction(data));
          return Promise.reject(data);
        });
    };
  }

  isNotEligible(data) {
    const eligibilityStatus = sanitizeString(data.eligibilityStatus ||
      data.deposit0.eligibilityStatus);
    return (eligibilityStatus === 'false');
  }

  redirectToNotEligiblePage(dispatch) {
    const testObject = {};
    testObject.response = {
      'status': 277,
      data: {
        errors: [{ code: DepositService.ELIGIBILITY_FAILED_ERROR_CODE }],
      },
    };
    throwCustomError(testObject, dispatch);
  }

  requestCompanyNameByAgreementKey(params, formValues) {
    return (dispatch, getState) => {
      if (this.shouldUpdate(getState())) {
        dispatch(DepositService.validateCompanyInfo());
        let mappedParams = params
          ? createParams(params, formValues, getState())
          : {};
        const errors = params ? validateParams(mappedParams, params) : [];

        if (errors.length > 0) {
          return Promise.reject(createErrorBody('Missing parameters', errors));
        }
        const agreementKeyParam =
          getValueFromObjects(
            'agreementKey',
            mappedParams,
            formValues
          );

        const companyAuthMissingErrorObj = {
          'code': 'co-auth-missing',
          'msg': 'Incorrect or No customer details found.',
        };

        let byPassFlag =
          getValueFromObjects(
            'byPassCompanyService',
            mappedParams,
            formValues
          ) || 'N';

        byPassFlag = byPassFlag === 'Y';

        if (!byPassFlag && (agreementKeyParam == null || typeof agreementKeyParam === 'undefined' ||
          agreementKeyParam.trim().length === 0)) {
          return Promise.reject(createErrorBody('NOT_AUTHORIZED', [companyAuthMissingErrorObj]));
        }
        mappedParams = {
          general: {
            globalTransactionID: uuid(),
          },
          agreementkey: agreementKeyParam,
        };

        return this.callApiPost(
          DepositService.COMPANY_INFO_BY_AGREEMENT_KEY,
          mappedParams,
          dispatch
        ).then((response) => {
          // Let the calling code know there's nothing to wait for.
          const companyNameStr = response.companyName;
          const hasData = companyNameStr && companyNameStr.length > 0;
          const currentDate = moment().format('DD-MM-YYYY');
          const maxLeaseStartDate = this.getMaximumAdvanceLeaseStartDate(currentDate,
            mappedParams, formValues);
          if (hasData) {
            return {
              companyName: companyNameStr,
              companyInfoValid: hasData,
              companyInfoNoService: false,
              maxLeaseStartDate,
              currentDate,
            };
          }
          if (!byPassFlag) {
            return Promise.reject(createErrorBody('NOT_AUTHORIZED', [companyAuthMissingErrorObj]));
          }
          return {
            companyName: null,
            companyInfoValid: hasData,
            companyInfoNoService: false,
          };
        });
      }

      // Let the calling code know there's nothing to wait for.
      return Promise.resolve();
    };
  }

  /**
   * This function returns maximum advance lease start date
   * that can be selected by the user in advance
   * @param inputDate
   * @param mappedParams
   * @param formValues
   * @returns {string}
   */
  getMaximumAdvanceLeaseStartDate(inputDate, mappedParams, formValues) {
    if (sanitizeString(inputDate) !== '' && isValidDate(inputDate)) {
      const daysToAdd = getValueFromObjects('advanceDaysToAddLeaseStartDate', mappedParams, formValues) || 0;
      const monthsToAdd = getValueFromObjects('advanceMonthsToAddLeaseStartDate', mappedParams, formValues) || 0;
      const yearsToAdd = getValueFromObjects('advanceYearsToAddLeaseStartDate', mappedParams, formValues) || 0;
      const newDateFormat = 'DD-MM-YYYY';
      const formattedDate = moment(inputDate, newDateFormat)
        .add(daysToAdd, 'days')
        .add(monthsToAdd, 'months')
        .add(yearsToAdd, 'years');

      return moment(formattedDate).format(newDateFormat);
    }
    return inputDate;
  }

  purchase(params, values) {
    return (dispatch, getState) => {
      const state = getState();
      const formValues = { ...values, ...state.userData };
      const mappedParams = params
        ? createParams(params, formValues, state)
        : {};
      const errors = params ? validateParams(mappedParams, params) : [];

      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }

      const requestModel = {
        leaseContractNo: getValueFromObjects('leaseNumber', mappedParams, formValues),
      };

      const mappedParams1 = {
        ...mappedParams,
        activationDate: getValueFromObjects('leaseStartdate', mappedParams, formValues),
      };
      return dispatch(super.purchaseV2(DepositService.DEPOSIT_PURCHASE,
        mappedParams1, requestModel, formValues));
    };
  }

  calculatePriceAndFee(price, feeList, formValues) {
    const paymentServiceFee =
      Math.round(this.getFeeAmountPerPayment(feeList,
        DepositService.PAYMENT_SERVICE_FEE_CODE) * 100) / 100;
    const guaranteeFee =
      Math.round(this.getFeeAmountPerPayment(feeList,
        DepositService.GUARANTEE_FEE_CODE) * 100) / 100;
    const priceWithFee = Math.round(price.premiumAmount * 100) / 100;

    return {
      premiumPrice: price.premiumWithOutFee,
      guaranteeFee: `${guaranteeFee}`,
      priceWithFee: `${priceWithFee}`,
      insuranceFee: price.insuranceToll,
      paymentFee: formValues.paymentFee || `${paymentServiceFee}`,
    };
  }

  prepareCreateUpdateResponse(payload, mappedParams, formValues) {
    const eligibilityStatus = payload.eligibility;
    const me = this;
    let response = prepareCommonCreateUpdateResponse.call(this, payload, formValues);
    response = {
      eligibilityStatus,
      ...response,
      KVHX: payload.rentedHousing.postalAddress.KVHX,
      priceApiResponse: [
        {
          offeredPrice: me.calculatePriceAndFee(
            payload.price.offeredPrice,
            payload.price.fees,
            formValues
          ),
          listPrice: me.calculatePriceAndFee(
            payload.price.listPrice,
            payload.price.fees,
            formValues
          ),
          advantagePrice: me.calculatePriceAndFee(
            payload.price.advantagePrice,
            payload.price.fees,
            formValues
          ),
          discountAmount:
            me.calculatePriceAndFee(
              payload.price.listPrice,
              payload.price.fees,
              formValues
            ).priceWithFee -
            me.calculatePriceAndFee(
              payload.price.offeredPrice,
              payload.price.fees,
              formValues
            ).priceWithFee,
        },
      ],
    };

    const listPrice = response.priceApiResponse[0].listPrice.premiumPrice;
    const offeredPrice = response.priceApiResponse[0].offeredPrice.premiumPrice;
    const discount = Math.round(((listPrice - offeredPrice) / listPrice) * 100);

    response.discount = `${discount < DepositService.MINIMUM_DISCOUNT ? DepositService.MINIMUM_DISCOUNT : discount}`;

    return response;
  }

  createUpdateDepositAction(params, values) {
    return (dispatch, getState) => {
      const { formValues, mappedParams } = this.mapUserdata(getState(), values, params);
      const requestModel = this.getRequestModel(mappedParams, formValues);
      const quote = 'quotes';
      if (formValues[quote]) {
        requestModel.generalInsurance.sessionKey = formValues[quote][formValues.chosenPackage].sessionKey || '';
      }
      requestModel.checkEligibility = true;
      const hasDog = getValueFromObjects('doYouHaveADog', mappedParams, formValues) === 'yes';
      requestModel.customer = {
        age: getCustomerAge(mappedParams, formValues),
        birthdate: getValueFromObjects('birthdate', mappedParams, formValues),
        civilRegistrationNumber: getCPRMapping(mappedParams, formValues),
        totalInsured: getValueFromObjects('totalInsured', mappedParams, formValues),
        rolePlayerId: getValueFromObjects('rolePlayerId', mappedParams, formValues),
        hasDog,
      };

      requestModel.housingAssociation = {
        internalAgreementKey: getValueFromObjects('agreementKey', mappedParams, formValues),
        housingAssociationName: getValueFromObjects('companyName', mappedParams, formValues),
      };
      const postalAddress = getAddressMappingV2(mappedParams, formValues);
      requestModel.rentedHousing = {
        occupiedArea: getValueFromObjects('leaseArea', mappedParams, formValues),
        postalAddress,
      };
      requestModel.packageText = getValueFromObjects('packageText', mappedParams, formValues) || 'Basis';
      requestModel.coverage = [];

      let rentedAddress = `${postalAddress.street} ${postalAddress.houseNumber}`;
      rentedAddress += postalAddress.floorNumber ?
        `, ${postalAddress.floorNumber}` : '';
      rentedAddress += postalAddress.unitNumber ?
        `. ${postalAddress.unitNumber}` : '';
      // eslint-disable-next-line no-param-reassign
      getState().userData.rentedAddress = rentedAddress;
      if (getState().userData.deposit) {
        return dispatch(
          this.updateDepositAction(mappedParams, requestModel, {
            ...formValues,
          })
        );
      }

      return dispatch(
        this.createDepositAction(mappedParams, requestModel, formValues)
      );
    };
  }

  createCustomer(params, values) {
    return (dispatch, getState) => {
      const state = getState();
      const formValues = { ...values, ...state.userData };
      const mappedParams = params
        ? createParams(params, formValues, state)
        : {};
      const errors = params ? validateParams(mappedParams, params) : [];

      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }
      const quote = 'quotes';
      const requestModel = this.getRequestModel(mappedParams, formValues);
      requestModel.generalInsurance.sessionKey = formValues[quote][formValues.chosenPackage].sessionKey || '';
      requestModel.consent = {
        // consentForCPR
        cprConsent: getValueFromObjects('consentForCPR', mappedParams, formValues) || false,
        dataExchangeWithPartnerConsent: getValueFromObjects('consentForRentInsurancePrivacyPolicy', mappedParams, formValues) || false,
        unionMemberShipConsent: getValueFromObjects('consentForBecomeACustomer', mappedParams, formValues) || false,
        promotionAcceptConsent: getValueFromObjects('consentForBecomeACustomer', mappedParams, formValues) || false,
        digitalCommunicationConsent: getValueFromObjects('consentForBecomeACustomer', mappedParams, formValues) || false,
      };
      requestModel.customer = {
        civilRegistrationNumber: getCPRMapping(mappedParams, formValues),
        firstName: getValueFromObjects('firstName', mappedParams, formValues),
        lastName: getValueFromObjects('lastName', mappedParams, formValues),
        emailAddress: getValueFromObjects('emailAddress', mappedParams, formValues),
        telephoneNumber: getValueFromObjects('telephoneNumber', mappedParams, formValues),
      };

      const CUSTOM_ADDRESS_KEY_VALUE_LIST = [{ 'findWith': 'street1', 'pairWith': 'street', 'defaultValue': '' },
        { 'findWith': 'houseNumber1', 'pairWith': 'houseNumber', 'defaultValue': '' },
        { 'findWith': 'toHouseNumber1', 'pairWith': 'toHouseNumber', 'defaultValue': '' },
        { 'findWith': 'floorNumber1', 'pairWith': 'floorNumber', 'defaultValue': '' },
        { 'findWith': 'city1', 'pairWith': 'city', 'defaultValue': '' },
        { 'findWith': 'postalCode1', 'pairWith': 'postalCode', 'defaultValue': '' },
        { 'findWith': 'country1', 'pairWith': 'country', 'defaultValue': '' },
        { 'findWith': 'countryCode1', 'pairWith': 'countryCode', 'defaultValue': '' },
        { 'findWith': 'KVHX1', 'pairWith': 'KVHX', 'defaultValue': '' },
      ];

      const customerAddress = getMapping(CUSTOM_ADDRESS_KEY_VALUE_LIST, mappedParams, formValues);
      requestModel.postalAddress = customerAddress;
      dispatch(this.createCustomerAction());

      return this.callApiPost(DepositService.CREATE_CUSTOMER, requestModel, dispatch)
        .then((data) => {
          dispatch(this.createdCustomerAction(data));
          return data;
        })
        .catch(data => {
          dispatch(appErrorAction(data));
          return Promise.reject(data);
        });
    };
  }
}

export const depositService = new DepositService();
